import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { Person } from '../types/people';
import { peopleApi } from '../services/api';

interface PeopleDataContextType {
  data: Person[];
  loading: boolean;
  error: Error | null;
  loadingMore: boolean;
  refetch: () => void;
  clearCache: () => void;
  getPersonById: (id: string) => Person | undefined;
}

const PeopleDataContext = createContext<PeopleDataContextType | undefined>(undefined);

interface PeopleDataProviderProps {
  children: ReactNode;
}

export const PeopleDataProvider: React.FC<PeopleDataProviderProps> = ({ children }) => {
  const [data, setData] = useState<Person[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Track if data has been fetched to prevent duplicate calls
  const hasFetchedRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchPeople = async (isRefetch: boolean = false) => {
    // If we already have data and this isn't a refetch, skip
    if (!isRefetch && hasFetchedRef.current && data.length > 0) {
      console.log('[PeopleDataContext] Using cached data');
      setLoading(false);
      return;
    }

    // Create abort controller for cleanup
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    try {
      setLoading(true);
      setError(null);
      
      // Mark as fetched before starting
      hasFetchedRef.current = true;
      
      // Step 1: Fetch first page immediately for fast initial render
      const firstPageResponse = await peopleApi.getIndividualsFirstPage();
      
      // Check if aborted
      if (signal.aborted) {
        setLoading(false);
        return;
      }
      
      // Validate response structure
      if (!firstPageResponse || !firstPageResponse.data || !Array.isArray(firstPageResponse.data)) {
        console.error('Invalid response structure:', firstPageResponse);
        throw new Error(`Invalid response from API: ${JSON.stringify(firstPageResponse)}`);
      }
              
      // Data is already formatted by the BFF, just use it
      const firstPagePeople = firstPageResponse.data as unknown as Person[];
      setData(firstPagePeople);
      setLoading(false); // Stop initial loading spinner
      
      console.log('[PeopleDataContext] Loaded first page:', firstPagePeople.length, 'people');
      
      // Step 2: If there are more pages, fetch them in the background
      if (firstPageResponse.hasMore) {
        setLoadingMore(true);          
        // Small delay to ensure UI renders first page first
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Check if aborted
        if (signal.aborted) {
          setLoadingMore(false);
          return;
        }
        
        const remainingResponse = await peopleApi.getIndividualsRemainingPages(2);
        
        // Check if aborted
        if (signal.aborted) {
          setLoadingMore(false);
          return;
        }
        
        // Validate response structure
        if (!remainingResponse || !remainingResponse.data || !Array.isArray(remainingResponse.data)) {
          console.error('Invalid remaining pages response:', remainingResponse);
          // Don't throw, just log and continue with first page data
          setLoadingMore(false);
          console.log(`⚠️ Could not load remaining pages. Showing ${firstPageResponse.data.length} individuals`);
          return;
        }
                  
        // Data is already formatted by the BFF, merge with existing data and deduplicate by ID
        const additionalPeople = remainingResponse.data as unknown as Person[];
        setData(prevData => {
          // Create a Map to deduplicate by ID
          const peopleMap = new Map<string, Person>();
          
          // Add existing people
          prevData.forEach(person => peopleMap.set(person.id, person));
          
          // Add new people (will overwrite if duplicate)
          let duplicateCount = 0;
          additionalPeople.forEach(person => {
            if (peopleMap.has(person.id)) {
              duplicateCount++;
            }
            peopleMap.set(person.id, person);
          });
          
          // Log warning if duplicates found
          if (duplicateCount > 0) {
            console.warn(
              `[PeopleDataContext] Found ${duplicateCount} duplicate person(s) when merging pages.`
            );
          }
          
          return Array.from(peopleMap.values());
        });
        setLoadingMore(false);
        
        const totalCount = firstPageResponse.data.length + remainingResponse.data.length;
        console.log(`✅ [PeopleDataContext] All data loaded! Total: ${totalCount} individuals`);
      } else {
        console.log(`✅ [PeopleDataContext] All data loaded! Total: ${firstPageResponse.data.length} individuals`);
      }
      
    } catch (err) {
      // Only update state if not aborted
      if (!signal.aborted) {
        console.error('[PeopleDataContext] Error fetching people:', err);
        
        // Enhanced error logging
        if (err instanceof Error) {
          console.error('Error details:', {
            message: err.message,
            stack: err.stack,
          });
        }
        
        // Check if it's an Axios error with response
        const axiosError = err as any;
        if (axiosError?.response) {
          console.error('API Response Error:', {
            status: axiosError.response.status,
            statusText: axiosError.response.statusText,
            data: axiosError.response.data,
            headers: axiosError.response.headers,
          });
          
          // Create a more helpful error message
          const errorMessage = axiosError.response.data?.error?.message 
            || axiosError.response.data?.message 
            || `API Error: ${axiosError.response.status} ${axiosError.response.statusText}`;
          
          setError(new Error(errorMessage));
        } else if (axiosError?.request) {
          // Request was made but no response received
          console.error('No response from server. Is the BFF API running?');
          setError(new Error('Cannot connect to API server. Please ensure the BFF API is running.'));
        } else {
          setError(err instanceof Error ? err : new Error('Failed to fetch people'));
        }
        
        setData([]);
        setLoading(false);
        setLoadingMore(false);
      }
    }
  };

  useEffect(() => {
    fetchPeople();

    // Cleanup function
    return () => {
      // Abort any pending requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []); // Empty dependency array - only run once

  const refetch = () => {
    hasFetchedRef.current = false;
    fetchPeople(true);
  };

  const clearCache = () => {
    setData([]);
    setLoading(true);
    setError(null);
    setLoadingMore(false);
    hasFetchedRef.current = false;
  };

  const value: PeopleDataContextType = {
    data,
    loading,
    error,
    loadingMore,
    refetch,
    clearCache,
    getPersonById: (id: string) => data.find(person => person.id === id),
  };

  return (
    <PeopleDataContext.Provider value={value}>
      {children}
    </PeopleDataContext.Provider>
  );
};

export const usePeopleDataContext = () => {
  const context = useContext(PeopleDataContext);
  if (context === undefined) {
    throw new Error('usePeopleDataContext must be used within a PeopleDataProvider');
  }
  return context;
};
